import { useEffect, useState } from "react";
import api from "../api"; // ✅ Central API

/* ------------------ helpers ------------------ */
function formatHours(decimal) {
  if (!decimal) return "00:00";
  let h = Math.floor(decimal);
  let m = Math.round((decimal - h) * 60);
  if (m === 60) {
    h++;
    m = 0;
  }
  return `${h}:${m.toString().padStart(2, "0")}`;
}

/**
 * ⚠️ IMPORTANT
 * Do NOT use toISOString() (UTC issue).
 * Build date string manually in LOCAL time.
 */
function getMonthDates(month, year) {
  const lastDate = new Date(year, month, 0).getDate();
  const dates = [];

  for (let d = 1; d <= lastDate; d++) {
    const mm = String(month).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    dates.push(`${year}-${mm}-${dd}`);
  }
  return dates;
}

function getDayName(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short",
  });
}

function isSunday(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).getDay() === 0;
}

function getStatus(row, dateStr) {
  if (isSunday(dateStr)) return "WO"; // Week Off
  if (row?.InTime1) return "P"; // Present
  return "A"; // Absent
}

/* ------------------ COMPONENT ------------------ */
function AttendancePage() {
  const now = new Date();

  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const [groupedData, setGroupedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNoData(false);

    api
      .get("/attendance/daily-monthly-report", {
        params: { month, year },
      })
      .then((res) => {
        const rows = res.data.dailyReport || [];
        const allDates = getMonthDates(month, year);

        if (rows.length === 0) {
          setGroupedData([]);
          setNoData(true);
          return;
        }

        const groups = {};

        // 1️⃣ Build employee map
        rows.forEach((r) => {
          if (!groups[r.EmployeeCode]) {
            groups[r.EmployeeCode] = {
              EmployeeCode: r.EmployeeCode,
              EmployeeName: r.EmployeeName,
              dayMap: {},
              days: [],
              totals: { Span: 0, Break: 0, Net: 0 },
            };
          }
          groups[r.EmployeeCode].dayMap[r.WorkDate] = r;
        });

        // 2️⃣ Fill missing dates
        Object.values(groups).forEach((emp) => {
          emp.days = allDates.map((dateStr) => {
            const row = emp.dayMap[dateStr] || {
              WorkDate: dateStr,
              InTime1: null,
              OutTime1: null,
              InTime2: null,
              OutTime2: null,
              TotalSpanHours: 0,
              BreakHours: 0,
              NetHours: 0,
            };

            emp.totals.Span += row.TotalSpanHours || 0;
            emp.totals.Break += row.BreakHours || 0;
            emp.totals.Net += row.NetHours || 0;

            return row;
          });
        });

        setGroupedData(Object.values(groups));
      })
      .catch((err) => {
        console.error(err);
        setNoData(true);
      })
      .finally(() => setLoading(false));
  }, [month, year]);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div style={{ padding: 20 }}>
      <h2>Employee Attendance Summary</h2>

      {/* Filters */}
      <div style={{ marginBottom: 20 }}>
        <select value={month} onChange={(e) => setMonth(+e.target.value)}>
          {monthNames.map((m, i) => (
            <option key={i + 1} value={i + 1}>
              {m}
            </option>
          ))}
        </select>

        <select
          value={year}
          onChange={(e) => setYear(+e.target.value)}
          style={{ marginLeft: 10 }}
        >
          {[2024, 2025, 2026].map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {loading && <p>Loading attendance…</p>}
      {noData && <p style={{ color: "red" }}>No attendance data available.</p>}

      {groupedData.map((emp) => (
        <div key={emp.EmployeeCode} style={{ marginBottom: 40 }}>
          <h3>
            {emp.EmployeeName} ({emp.EmployeeCode})
          </h3>

          <table border="1" cellPadding="8" width="100%">
            <thead>
              <tr>
                <th>Date</th>
                <th>Day</th>
                <th>Status</th>
                <th>In1</th>
                <th>Out1</th>
                <th>In2</th>
                <th>Out2</th>
                <th>Span</th>
                <th>Break</th>
                <th>Net</th>
              </tr>
            </thead>

            <tbody>
              {emp.days.map((r, i) => {
                const status = getStatus(r, r.WorkDate);
                return (
                  <tr
                    key={i}
                    style={{
                      background:
                        status === "WO"
                          ? "#f2f2f2"
                          : status === "A"
                          ? "#fff3cd"
                          : "transparent",
                    }}
                  >
                    <td>{r.WorkDate}</td>
                    <td>{getDayName(r.WorkDate)}</td>
                    <td>
                      <b>{status}</b>
                    </td>
                    <td>{r.InTime1 || "-"}</td>
                    <td>{r.OutTime1 || "-"}</td>
                    <td>{r.InTime2 || "-"}</td>
                    <td>{r.OutTime2 || "-"}</td>
                    <td>{formatHours(r.TotalSpanHours)}</td>
                    <td>{formatHours(r.BreakHours)}</td>
                    <td>{formatHours(r.NetHours)}</td>
                  </tr>
                );
              })}

              <tr style={{ fontWeight: "bold", background: "#eaffea" }}>
                <td colSpan="7" align="right">
                  Monthly Total →
                </td>
                <td>{formatHours(emp.totals.Span)}</td>
                <td>{formatHours(emp.totals.Break)}</td>
                <td>{formatHours(emp.totals.Net)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

export default AttendancePage;
